import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useContextSystem } from '@wp-g2/context';
import { css, cx, ui } from '@wp-g2/styles';
import { is, noop, useControlledValue, useResizeAware } from '@wp-g2/utils';
import React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useBaseField } from '../BaseField';
import { useFormGroupContextId } from '../FormGroup';
import * as ScrollableStyles from '../Scrollable/Scrollable.styles';
import * as TextInputStyles from '../TextInput/TextInput.styles';
import * as styles from './Select.styles';
import { renderContent } from './Select.utils';
var PLACEHOLDER_VALUE = '';
/**
 * @param {import('@wp-g2/create-styles').ViewOwnProps<import('./types').Props, 'select'>} props
 */

export function useSelect(props) {
  var _useContextSystem = useContextSystem(props, 'Select'),
      children = _useContextSystem.children,
      className = _useContextSystem.className,
      _useContextSystem$def = _useContextSystem.defaultValue,
      defaultValue = _useContextSystem$def === void 0 ? PLACEHOLDER_VALUE : _useContextSystem$def,
      disabled = _useContextSystem.disabled,
      _useContextSystem$err = _useContextSystem.error,
      error = _useContextSystem$err === void 0 ? false : _useContextSystem$err,
      idProp = _useContextSystem.id,
      _useContextSystem$isI = _useContextSystem.isInline,
      isInline = _useContextSystem$isI === void 0 ? false : _useContextSystem$isI,
      _useContextSystem$isF = _useContextSystem.isFocused,
      isFocusedProp = _useContextSystem$isF === void 0 ? false : _useContextSystem$isF,
      isSubtle = _useContextSystem.isSubtle,
      multiple = _useContextSystem.multiple,
      _useContextSystem$onB = _useContextSystem.onBlur,
      onBlur = _useContextSystem$onB === void 0 ? noop : _useContextSystem$onB,
      _useContextSystem$onC = _useContextSystem.onChange,
      onChangeProp = _useContextSystem$onC === void 0 ? noop : _useContextSystem$onC,
      _useContextSystem$onF = _useContextSystem.onFocus,
      onFocus = _useContextSystem$onF === void 0 ? noop : _useContextSystem$onF,
      _useContextSystem$opt = _useContextSystem.options,
      options = _useContextSystem$opt === void 0 ? [] : _useContextSystem$opt,
      placeholderProp = _useContextSystem.placeholder,
      prefix = _useContextSystem.prefix,
      size = _useContextSystem.size,
      suffix = _useContextSystem.suffix,
      valueProp = _useContextSystem.value,
      otherProps = _objectWithoutProperties(_useContextSystem, ["children", "className", "defaultValue", "disabled", "error", "id", "isInline", "isFocused", "isSubtle", "multiple", "onBlur", "onChange", "onFocus", "options", "placeholder", "prefix", "size", "suffix", "value"]);

  var _useControlledValue = useControlledValue({
    value: valueProp,
    onChange: onChangeProp,
    defaultValue: multiple ? [] : defaultValue
  }),
      _useControlledValue2 = _slicedToArray(_useControlledValue, 2),
      value = _useControlledValue2[0],
      onChange = _useControlledValue2[1];

  var _useState = useState(isFocusedProp),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];

  var _useResizeAware = useResizeAware(),
      _useResizeAware2 = _slicedToArray(_useResizeAware, 2),
      resizer = _useResizeAware2[0],
      sizes = _useResizeAware2[1];
  /** @type {import('react').Ref<HTMLSelectElement | undefined>} */


  var inputRef = useRef();
  var baseFieldProps = useBaseField({
    disabled: disabled,
    error: error,
    gap: 0,
    isClickable: true,
    isFocused: isFocused,
    isInline: isInline,
    isSubtle: isSubtle
  });
  var id = useFormGroupContextId(idProp);
  var handleOnRootClick = useCallback(function () {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  var handleOnBlur = useCallback(function (
  /** @type {import('react').FocusEvent<HTMLSelectElement>} */
  event) {
    onBlur(event);
    setIsFocused(false);
  }, [onBlur]);
  var handleOnFocus = useCallback(function (
  /** @type {import('react').FocusEvent<HTMLSelectElement>} */
  event) {
    onFocus(event);
    setIsFocused(true);
  }, [onFocus]);
  var handleOnChange = useCallback(function (
  /** @type {import('react').ChangeEvent<HTMLSelectElement>} */
  event) {
    var next;

    if (multiple) {
      next = Array.from(event.target.options).filter(function (_ref) {
        var selected = _ref.selected;
        return selected;
      }).map(function (_ref2) {
        var value = _ref2.value;
        return value;
      });
    } else {
      next = event.target.value;
    }

    onChange(next, {
      event: event
    });
  }, [multiple, onChange]);
  var shouldRenderPlaceholder = (!is.defined(value) || is.empty(value)) && typeof placeholderProp === 'string';
  var classes = cx(baseFieldProps.className, styles.base, className);
  var hasArrow = !multiple;
  var paddingRight = hasArrow ? styles.ARROW_WRAPPER_WIDTH : 0;
  var inputClasses = cx(TextInputStyles.Input, styles.select, shouldRenderPlaceholder && styles.placeholder, multiple && ScrollableStyles.scrollableScrollbar, TextInputStyles[size], css({
    paddingRight: sizes.width || 0 + paddingRight
  }));
  var content = renderContent({
    children: children,
    options: options
  });

  if (shouldRenderPlaceholder) {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("option", {
      disabled: true,
      value: PLACEHOLDER_VALUE
    }, placeholderProp), content);
  }

  var inputProps = _objectSpread(_objectSpread({
    children: content,
    className: inputClasses,
    id: id,
    disabled: disabled,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    multiple: multiple,
    value: value
  }, ui.$('Select')), otherProps);

  var suffixClasses = cx(styles.Suffix, hasArrow && styles.arrowPadding);
  return _objectSpread(_objectSpread(_objectSpread({}, baseFieldProps), ui.$('SelectWrapper')), {}, {
    className: classes,
    onClick: handleOnRootClick,
    inputProps: inputProps,
    multiple: multiple,
    prefix: prefix,
    inputRef: inputRef,
    resizer: resizer,
    suffixProps: {
      className: suffixClasses
    },
    sizes: sizes,
    suffix: suffix
  });
}